import {
  addDoc,
  collection,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  where,
  type Firestore,
  type QueryConstraint,
} from 'firebase/firestore'
import {
  coerceAuditLog,
  mergeAuditLogSnapshots,
  type AuditDomain,
  type AuditLog,
  type AuditLogDoc,
} from '@shared/lib/auditLogs'
import { stripUndefined } from './stripUndefined'

export interface AuditLogsDayQuery {
  startIso: string
  endIso: string
  domain?: AuditDomain
  /** Positive cap. Omit to fetch every event in the day window. */
  limit?: number
}

export async function writeAuditLog(
  placeId: string,
  db: Firestore,
  entry: AuditLog,
): Promise<void> {
  const ref = collection(db, 'places', placeId, 'auditLogs')
  // serverAt is authoritative: `at` comes from the device clock and can be shifted.
  await addDoc(ref, {
    ...stripUndefined(entry as unknown as Record<string, unknown>),
    serverAt: serverTimestamp(),
  })
}

export function subscribeAuditLogsForDay(
  placeId: string,
  db: Firestore,
  opts: AuditLogsDayQuery,
  onLogs: (logs: AuditLogDoc[]) => void,
): () => void {
  const ref = collection(db, 'places', placeId, 'auditLogs')
  const max = typeof opts.limit === 'number' && opts.limit > 0 ? opts.limit : undefined
  const domain = opts.domain
  const startAt = new Date(opts.startIso)
  const endAt = new Date(opts.endIso)
  const withCap = (constraints: QueryConstraint[]): QueryConstraint[] =>
    max != null ? [...constraints, limit(max)] : constraints
  const domainEq = (constraints: QueryConstraint[]): QueryConstraint[] =>
    domain ? [where('domain', '==', domain), ...constraints] : constraints
  // Server clock is authoritative. A second `at` query surfaces legacy docs
  // written before `serverAt` existed (Firestore skips docs missing the field).
  const serverAtQuery = query(
    ref,
    ...withCap(
      domainEq([
        where('serverAt', '>=', startAt),
        where('serverAt', '<', endAt),
        orderBy('serverAt', 'desc'),
      ]),
    ),
  )
  const deviceAtQuery = query(
    ref,
    ...withCap(
      domainEq([
        where('at', '>=', opts.startIso),
        where('at', '<', opts.endIso),
        orderBy('at', 'desc'),
      ]),
    ),
  )
  let serverAtLogs: AuditLogDoc[] | null = null
  let deviceAtLogs: AuditLogDoc[] | null = null

  const emit = () => {
    if (serverAtLogs === null || deviceAtLogs === null) return
    const merged = mergeAuditLogSnapshots(serverAtLogs, deviceAtLogs, max)
    onLogs(domain ? merged.filter((log) => log.domain === domain) : merged)
  }

  const unsubServerAt = onSnapshot(
    serverAtQuery,
    (snap) => {
      serverAtLogs = snap.docs.map((d) => coerceAuditLog(d.id, d.data()))
      emit()
    },
    (err) => {
      console.error('[auditLogs] serverAt subscribe failed', err)
    },
  )
  const unsubDeviceAt = onSnapshot(
    deviceAtQuery,
    (snap) => {
      deviceAtLogs = snap.docs.map((d) => coerceAuditLog(d.id, d.data()))
      emit()
    },
    (err) => {
      console.error('[auditLogs] at subscribe failed', err)
    },
  )
  return () => {
    unsubServerAt()
    unsubDeviceAt()
  }
}

/** One-shot history for an order detail modal. Not a live listener. */
export async function fetchAuditLogsForOrder(
  placeId: string,
  db: Firestore,
  orderId: string,
  max = 50,
): Promise<AuditLogDoc[]> {
  const ref = collection(db, 'places', placeId, 'auditLogs')
  const cap = max > 0 ? max : 50
  const [serverSnap, deviceSnap] = await Promise.all([
    getDocs(query(ref, where('orderId', '==', orderId), orderBy('serverAt', 'desc'), limit(cap))),
    getDocs(query(ref, where('orderId', '==', orderId), orderBy('at', 'desc'), limit(cap))),
  ])
  return mergeAuditLogSnapshots(
    serverSnap.docs.map((d) => coerceAuditLog(d.id, d.data())),
    deviceSnap.docs.map((d) => coerceAuditLog(d.id, d.data())),
    cap,
  )
}

/** One-shot history for a bill details modal. Not a live listener. */
export async function fetchAuditLogsForBill(
  placeId: string,
  db: Firestore,
  billId: string,
  max = 50,
): Promise<AuditLogDoc[]> {
  const ref = collection(db, 'places', placeId, 'auditLogs')
  const cap = max > 0 ? max : 50
  const [serverSnap, deviceSnap] = await Promise.all([
    getDocs(query(ref, where('billId', '==', billId), orderBy('serverAt', 'desc'), limit(cap))),
    getDocs(query(ref, where('billId', '==', billId), orderBy('at', 'desc'), limit(cap))),
  ])
  return mergeAuditLogSnapshots(
    serverSnap.docs.map((d) => coerceAuditLog(d.id, d.data())),
    deviceSnap.docs.map((d) => coerceAuditLog(d.id, d.data())),
    cap,
  )
}

/** One-shot audit rows in a time window. Used to fill reasons the bill log never stored. */
export async function fetchAuditLogsInRange(
  placeId: string,
  db: Firestore,
  opts: AuditLogsDayQuery,
): Promise<AuditLogDoc[]> {
  const ref = collection(db, 'places', placeId, 'auditLogs')
  const max = typeof opts.limit === 'number' && opts.limit > 0 ? opts.limit : undefined
  const domain = opts.domain
  const startAt = new Date(opts.startIso)
  const endAt = new Date(opts.endIso)
  const withCap = (constraints: QueryConstraint[]): QueryConstraint[] =>
    max != null ? [...constraints, limit(max)] : constraints
  const domainEq = (constraints: QueryConstraint[]): QueryConstraint[] =>
    domain ? [where('domain', '==', domain), ...constraints] : constraints
  const [serverSnap, deviceSnap] = await Promise.all([
    getDocs(
      query(
        ref,
        ...withCap(
          domainEq([
            where('serverAt', '>=', startAt),
            where('serverAt', '<', endAt),
            orderBy('serverAt', 'desc'),
          ]),
        ),
      ),
    ),
    getDocs(
      query(
        ref,
        ...withCap(
          domainEq([
            where('at', '>=', opts.startIso),
            where('at', '<', opts.endIso),
            orderBy('at', 'desc'),
          ]),
        ),
      ),
    ),
  ])
  const merged = mergeAuditLogSnapshots(
    serverSnap.docs.map((d) => coerceAuditLog(d.id, d.data())),
    deviceSnap.docs.map((d) => coerceAuditLog(d.id, d.data())),
    max,
  )
  return domain ? merged.filter((log) => log.domain === domain) : merged
}
